/**
 * Result/Either Monad pattern for predictable error handling.
 * Avoids throwing exceptions for expected business errors.
 */

export type Result<T, E = Error> = Success<T, E> | Failure<T, E>;

export class Success<T, E = Error> {
  readonly isSuccess = true;
  readonly isFailure = false;

  constructor(readonly value: T) {}

  unwrap(): T {
    return this.value;
  }

  unwrapOr(_defaultValue: T): T {
    return this.value;
  }

  map<U>(fn: (value: T) => U): Result<U, E> {
    return new Success<U, E>(fn(this.value));
  }

  mapError<U>(_fn: (error: E) => U): Result<T, U> {
    return new Success<T, U>(this.value);
  }
}

export class Failure<T, E = Error> {
  readonly isSuccess = false;
  readonly isFailure = true;

  constructor(readonly error: E) {}

  unwrap(): T {
    throw this.error;
  }

  unwrapOr(defaultValue: T): T {
    return defaultValue;
  }

  map<U>(_fn: (value: T) => U): Result<U, E> {
    return new Failure<U, E>(this.error);
  }

  mapError<U>(fn: (error: E) => U): Result<T, U> {
    return new Failure<T, U>(fn(this.error));
  }
}

export const ok = <T, E = Error>(value: T): Result<T, E> => new Success(value);
export const fail = <T, E = Error>(error: E): Result<T, E> => new Failure(error);
